import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  cartCount,
  cartSubtotal,
  lineKey,
  type CartLine,
  type Product,
  type SelectedOptions,
} from '@/lib/products';

/**
 * Per-card cart. Lines are {productId, qty, options}; the product snapshot is
 * resolved against the live product list on every render so a price edit by
 * the owner is reflected immediately and removed / unpublished products
 * silently drop out. A product with different option picks is a separate
 * line (keyed by product id + options).
 *
 * Persisted in localStorage per card so a visitor who taps away to Venmo and
 * comes back still has their order in progress.
 */

interface StoredLine {
  id: string;
  qty: number;
  options?: SelectedOptions;
}

export interface CartApi {
  lines: CartLine[];
  count: number;
  subtotal: number;
  add: (product: Product, options?: SelectedOptions, qty?: number) => void;
  setQty: (lineKey: string, qty: number) => void;
  remove: (lineKey: string) => void;
  clear: () => void;
  /** Total quantity of a product across all its option variants. */
  qtyOf: (productId: string) => number;
}

const CartContext = createContext<CartApi | null>(null);

export function useCart(): CartApi | null {
  return useContext(CartContext);
}

function storageKey(cardId: string) {
  return `haphe-cart-${cardId}`;
}

function cleanOptions(v: unknown): SelectedOptions | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const out: SelectedOptions = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string' && val) out[k] = val;
  }
  return Object.keys(out).length ? out : undefined;
}

function load(cardId: string): StoredLine[] {
  try {
    const raw = window.localStorage.getItem(storageKey(cardId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((l): l is StoredLine => !!l && typeof l === 'object' && typeof (l as StoredLine).id === 'string')
      .map((l) => ({
        id: l.id,
        qty: Math.max(1, Math.min(99, Math.round(Number(l.qty) || 1))),
        options: cleanOptions(l.options),
      }));
  } catch {
    return [];
  }
}

export function CartProvider({
  cardId,
  products,
  persist = true,
  onAdd,
  children,
}: {
  cardId: string;
  products: Product[];
  /** Off in the editor preview so the owner's own taps don't leave a cart behind. */
  persist?: boolean;
  onAdd?: (product: Product) => void;
  children: React.ReactNode;
}) {
  const [stored, setStored] = useState<StoredLine[]>(() =>
    persist && typeof window !== 'undefined' ? load(cardId) : [],
  );

  useEffect(() => {
    if (!persist || typeof window === 'undefined') return;
    try {
      if (stored.length === 0) window.localStorage.removeItem(storageKey(cardId));
      else window.localStorage.setItem(storageKey(cardId), JSON.stringify(stored));
    } catch {
      /* storage unavailable */
    }
  }, [stored, cardId, persist]);

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const lines = useMemo<CartLine[]>(
    () =>
      stored
        .map((l) => {
          const product = byId.get(l.id);
          if (!product || !product.available || !product.published) return null;
          return { key: lineKey(l.id, l.options), product, qty: l.qty, options: l.options ?? {} };
        })
        .filter((l): l is CartLine => l !== null),
    [stored, byId],
  );

  const add = useCallback(
    (product: Product, options?: SelectedOptions, qty = 1) => {
      const opts = cleanOptions(options);
      const key = lineKey(product.id, opts);
      setStored((cur) => {
        const existing = cur.find((l) => lineKey(l.id, l.options) === key);
        if (existing) {
          return cur.map((l) =>
            lineKey(l.id, l.options) === key ? { ...l, qty: Math.min(99, l.qty + qty) } : l,
          );
        }
        return [...cur, { id: product.id, qty: Math.min(99, Math.max(1, qty)), options: opts }];
      });
      onAdd?.(product);
    },
    [onAdd],
  );

  const setQty = useCallback((key: string, qty: number) => {
    setStored((cur) => {
      if (qty <= 0) return cur.filter((l) => lineKey(l.id, l.options) !== key);
      const clamped = Math.min(99, Math.round(qty));
      return cur.map((l) => (lineKey(l.id, l.options) === key ? { ...l, qty: clamped } : l));
    });
  }, []);

  const remove = useCallback((key: string) => {
    setStored((cur) => cur.filter((l) => lineKey(l.id, l.options) !== key));
  }, []);

  const clear = useCallback(() => setStored([]), []);

  const api = useMemo<CartApi>(
    () => ({
      lines,
      count: cartCount(lines),
      subtotal: cartSubtotal(lines),
      add,
      setQty,
      remove,
      clear,
      qtyOf: (id) => lines.filter((l) => l.product.id === id).reduce((s, l) => s + l.qty, 0),
    }),
    [lines, add, setQty, remove, clear],
  );

  return <CartContext.Provider value={api}>{children}</CartContext.Provider>;
}
